import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import {
  TrendingUp,
  Download,
  Clock,
  BarChart3,
  TrendingDown,
  GitMerge,
  Activity,
  Target,
  Info,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
  Area,
  AreaChart,
  ScatterChart,
  Scatter,
  Cell,
  ReferenceLine,
} from "recharts";

// Binance Color Scheme
const BINANCE_COLORS = {
  primary: "#0B0E11",
  secondary: "#1E2026",
  accent: "#FCD535",
  green: "#02C076",
  red: "#F6465D",
  blue: "#1FCFF1",
  orange: "#FF8C00",
  purple: "#B7A9F9",
  gray: "#474D57",
  textPrimary: "#EAECEF",
  textSecondary: "#848E9C",
};

// API Services
const fetchBinanceKlines = async (symbol, interval = "1h", limit = 100) => {
  try {
    const response = await fetch(
      `https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`
    );
    const data = await response.json();

    return data.map((item) => ({
      timestamp: parseInt(item[0]),
      open: parseFloat(item[1]),
      high: parseFloat(item[2]),
      low: parseFloat(item[3]),
      close: parseFloat(item[4]),
      volume: parseFloat(item[5]),
    }));
  } catch (error) {
    console.error(`Error fetching data for ${symbol}:`, error);
    return [];
  }
};

const fetch24hrTicker = async (symbol) => {
  try {
    const response = await fetch(
      `https://fapi.binance.com/fapi/v1/ticker/24hr?symbol=${symbol}`
    );
    return await response.json();
  } catch (error) {
    console.error(`Error fetching 24hr ticker for ${symbol}:`, error);
    return null;
  }
};

// Mathematical Functions
const calculateCorrelation = (prices1, prices2) => {
  if (prices1.length !== prices2.length || prices1.length < 2) return 0;

  const returns1 = prices1
    .slice(1)
    .map((price, i) => (price - prices1[i]) / prices1[i]);
  const returns2 = prices2
    .slice(1)
    .map((price, i) => (price - prices2[i]) / prices2[i]);

  const mean1 = returns1.reduce((sum, r) => sum + r, 0) / returns1.length;
  const mean2 = returns2.reduce((sum, r) => sum + r, 0) / returns2.length;

  let numerator = 0;
  let sumSq1 = 0;
  let sumSq2 = 0;

  for (let i = 0; i < returns1.length; i++) {
    const diff1 = returns1[i] - mean1;
    const diff2 = returns2[i] - mean2;
    numerator += diff1 * diff2;
    sumSq1 += diff1 * diff1;
    sumSq2 += diff2 * diff2;
  }

  const denominator = Math.sqrt(sumSq1 * sumSq2);
  return denominator === 0 ? 0 : numerator / denominator;
};

const calculateZScore = (spreads) => {
  if (spreads.length < 2) return 0;

  const mean = spreads.reduce((sum, s) => sum + s, 0) / spreads.length;
  const variance =
    spreads.reduce((sum, s) => sum + Math.pow(s - mean, 2), 0) / spreads.length;
  const std = Math.sqrt(variance);

  return std === 0 ? 0 : (spreads[spreads.length - 1] - mean) / std;
};

const calculateBeta = (prices1, prices2) => {
  if (prices1.length !== prices2.length || prices1.length < 2) return 1;

  const returns1 = prices1
    .slice(1)
    .map((price, i) => (price - prices1[i]) / prices1[i]);
  const returns2 = prices2
    .slice(1)
    .map((price, i) => (price - prices2[i]) / prices2[i]);

  const mean1 = returns1.reduce((sum, r) => sum + r, 0) / returns1.length;
  const mean2 = returns2.reduce((sum, r) => sum + r, 0) / returns2.length;

  let covariance = 0;
  let variance2 = 0;

  for (let i = 0; i < returns1.length; i++) {
    covariance += (returns1[i] - mean1) * (returns2[i] - mean2);
    variance2 += Math.pow(returns2[i] - mean2, 2);
  }

  return variance2 === 0
    ? 1
    : covariance / returns1.length / (variance2 / returns2.length);
};

// Advanced Statistical Functions
const calculateHalfLife = (spreads) => {
  if (spreads.length < 20) return 0;

  // Calculate lag-1 autocorrelation
  const laggedSpreads = spreads.slice(0, -1);
  const currentSpreads = spreads.slice(1);

  const correlation = calculateCorrelation(laggedSpreads, currentSpreads);

  // Half-life formula: -ln(2) / ln(correlation)
  if (correlation <= 0 || correlation >= 1) return 0;
  return -Math.log(2) / Math.log(correlation);
};

const calculateHurstExponent = (spreads) => {
  if (spreads.length < 50) return 0.5;

  const n = spreads.length;
  const mean = spreads.reduce((sum, s) => sum + s, 0) / n;

  // Calculate cumulative deviations
  let cumDev = 0;
  const cumDeviations = [];
  for (let i = 0; i < n; i++) {
    cumDev += spreads[i] - mean;
    cumDeviations.push(cumDev);
  }

  // Calculate range and standard deviation
  const maxCumDev = Math.max(...cumDeviations);
  const minCumDev = Math.min(...cumDeviations);
  const range = maxCumDev - minCumDev;

  const variance =
    spreads.reduce((sum, s) => sum + Math.pow(s - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);

  if (stdDev === 0) return 0.5;

  const rs = range / stdDev;
  return Math.log(rs) / Math.log(n);
};

const calculateCointegration = (prices1, prices2) => {
  if (prices1.length !== prices2.length || prices1.length < 30)
    return { isCointegrated: false, pValue: 1, testStatistic: 0 };

  // Simple linear regression to find cointegration relationship
  const n = prices1.length;
  const meanX = prices1.reduce((sum, p) => sum + p, 0) / n;
  const meanY = prices2.reduce((sum, p) => sum + p, 0) / n;

  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < n; i++) {
    const diffX = prices1[i] - meanX;
    const diffY = prices2[i] - meanY;
    numerator += diffX * diffY;
    denominator += diffX * diffX;
  }

  const beta = denominator === 0 ? 1 : numerator / denominator;
  const alpha = meanY - beta * meanX;

  // Calculate residuals (spread)
  const residuals = [];
  for (let i = 0; i < n; i++) {
    residuals.push(prices2[i] - (alpha + beta * prices1[i]));
  }

  // Augmented Dickey-Fuller test approximation
  const laggedResiduals = residuals.slice(0, -1);
  const deltaResiduals = residuals.slice(1).map((r, i) => r - residuals[i]);

  if (laggedResiduals.length === 0)
    return { isCointegrated: false, pValue: 1, testStatistic: 0 };

  const correlation = calculateCorrelation(laggedResiduals, deltaResiduals);
  const testStatistic =
    (correlation * Math.sqrt(n - 2)) / Math.sqrt(1 - correlation * correlation);

  // Approximate critical values for ADF test
  const criticalValue = -2.86; // 5% significance level
  const isCointegrated = testStatistic < criticalValue;
  const pValue = isCointegrated ? 0.03 : 0.08; // Simplified p-value

  return {
    isCointegrated,
    pValue,
    testStatistic,
    beta,
    alpha,
  };
};

// Parameter Optimization Function

const optimizeParameters = (priceData1, priceData2, iterations = 50) => {
  if (!priceData1.length || !priceData2.length) return null;

  let bestParams = null;
  let bestReturns = -Infinity; // Changed from bestSharpe

  const entryThresholds = [];
  for (let i = 1.5; i <= 3.0; i += 0.1) {
    entryThresholds.push(Math.round(i * 10) / 10);
  }

  const exitThresholds = [];
  for (let i = 0.2; i <= 1.0; i += 0.1) {
    exitThresholds.push(Math.round(i * 10) / 10);
  }

  const stopLosses = [];
  for (let i = 3.0; i <= 5.0; i += 0.2) {
    stopLosses.push(Math.round(i * 10) / 10);
  }

  for (const entry of entryThresholds) {
    for (const exit of exitThresholds) {
      for (const stop of stopLosses) {
        if (exit >= entry || stop <= entry) continue;

        const settings = {
          entryThreshold: entry,
          exitThreshold: exit,
          stopLoss: stop,
          takeProfit: entry + 1,
          transactionCost: 0.0004,
        };

        const result = runBacktest(priceData1, priceData2, settings);

        // Option 1: Optimize for total returns
        if (
          result &&
          result.totalReturn > bestReturns &&
          result.totalTrades >= 5
        ) {
          bestReturns = result.totalReturn;
          bestParams = { ...settings, result };
        }

        // Option 2: Optimize for annualized returns
        // if (result && result.annualizedReturn > bestReturns && result.totalTrades >= 5) {
        //     bestReturns = result.annualizedReturn;
        //     bestParams = { ...settings, result };
        // }

        // Option 3: Optimize for average return per trade
        // if (result && result.avgReturnPerTrade > bestReturns && result.totalTrades >= 5) {
        //     bestReturns = result.avgReturnPerTrade;
        //     bestParams = { ...settings, result };
        // }
      }
    }
  }

  return bestParams;
};

// Copula Analysis Functions
const calculateCopulaData = (prices1, prices2) => {
  if (prices1.length !== prices2.length || prices1.length < 10)
    return { copulaPoints: [], dependence: 0 };

  const returns1 = prices1
    .slice(1)
    .map((price, i) => (price - prices1[i]) / prices1[i]);
  const returns2 = prices2
    .slice(1)
    .map((price, i) => (price - prices2[i]) / prices2[i]);

  // Sort returns to calculate empirical cumulative distribution
  const sorted1 = [...returns1].sort((a, b) => a - b);
  const sorted2 = [...returns2].sort((a, b) => a - b);

  // Calculate uniform marginals (empirical CDF)
  const u1 = returns1.map(
    (r) => (sorted1.findIndex((x) => x >= r) + 1) / sorted1.length
  );
  const u2 = returns2.map(
    (r) => (sorted2.findIndex((x) => x >= r) + 1) / sorted2.length
  );

  // Create copula points
  const copulaPoints = u1.map((u, i) => ({
    u1: u,
    u2: u2[i],
    density: (u + u2[i]) / 2, // Simplified density
  }));

  // Calculate Kendall's tau for dependence measure
  let concordant = 0;
  let discordant = 0;

  for (let i = 0; i < returns1.length; i++) {
    for (let j = i + 1; j < returns1.length; j++) {
      const sign1 = Math.sign(returns1[i] - returns1[j]);
      const sign2 = Math.sign(returns2[i] - returns2[j]);

      if (sign1 === sign2) concordant++;
      else discordant++;
    }
  }

  const kendallTau = (concordant - discordant) / (concordant + discordant);

  return {
    copulaPoints: copulaPoints.slice(0, 100), // Limit points for visualization
    dependence: kendallTau,
    tailDependence: Math.abs(kendallTau * 0.7), // Simplified tail dependence
    copulaType:
      kendallTau > 0.3 ? "Gaussian" : kendallTau > 0 ? "Clayton" : "Gumbel",
  };
};

// Backtesting Functions
const runBacktest = (priceData1, priceData2, settings) => {
  if (!priceData1.length || !priceData2.length) return null;

  const minLength = Math.min(priceData1.length, priceData2.length);
  const prices1 = priceData1.slice(-minLength).map((k) => k.close);
  const prices2 = priceData2.slice(-minLength).map((k) => k.close);

  const trades = [];
  let position = null; // { type: 'long'|'short', entry: number, entryZScore: number, entryDate: string }
  let totalReturn = 0;
  let wins = 0;
  let losses = 0;
  let maxDrawdown = 0;
  let peakValue = 1;
  let dailyReturns = [];
  let equityCurve = [{ date: priceData1[0]?.timestamp, value: 1, drawdown: 0 }];

  // Calculate rolling statistics
  for (let i = 20; i < minLength; i++) {
    const currentPrices1 = prices1.slice(0, i + 1);
    const currentPrices2 = prices2.slice(0, i + 1);

    // Calculate returns
    const returns1 = currentPrices1
      .slice(1)
      .map((price, idx) => (price - currentPrices1[idx]) / currentPrices1[idx]);
    const returns2 = currentPrices2
      .slice(1)
      .map((price, idx) => (price - currentPrices2[idx]) / currentPrices2[idx]);

    // Calculate spread for last 20 periods
    const recentReturns1 = returns1.slice(-20);
    const recentReturns2 = returns2.slice(-20);
    const spreads = recentReturns1.map((r1, idx) => r1 - recentReturns2[idx]);

    const currentZScore = calculateZScore(spreads);
    const currentDate = new Date(priceData1[i]?.timestamp).toLocaleDateString();

    // Entry Logic
    if (!position) {
      if (currentZScore > settings.entryThreshold) {
        // Short the spread (short asset1, long asset2)
        position = {
          type: "short",
          entry: i,
          entryZScore: currentZScore,
          entryDate: currentDate,
          entryPrice1: prices1[i],
          entryPrice2: prices2[i],
        };
      } else if (currentZScore < -settings.entryThreshold) {
        // Long the spread (long asset1, short asset2)
        position = {
          type: "long",
          entry: i,
          entryZScore: currentZScore,
          entryDate: currentDate,
          entryPrice1: prices1[i],
          entryPrice2: prices2[i],
        };
      }
    }
    // Exit Logic
    else {
      let shouldExit = false;
      let exitReason = "";

      // Mean reversion exit
      if (Math.abs(currentZScore) < settings.exitThreshold) {
        shouldExit = true;
        exitReason = "Mean Reversion";
      }
      // Stop loss
      else if (Math.abs(currentZScore) > settings.stopLoss) {
        shouldExit = true;
        exitReason = "Stop Loss";
      }
      // Take profit (opposite direction)
      else if (
        (position.type === "long" && currentZScore < -settings.takeProfit) ||
        (position.type === "short" && currentZScore > settings.takeProfit)
      ) {
        shouldExit = true;
        exitReason = "Take Profit";
      }

      if (shouldExit) {
        const exitPrice1 = prices1[i];
        const exitPrice2 = prices2[i];

        // Calculate trade return
        let tradeReturn = 0;
        if (position.type === "long") {
          // Long spread: profit when spread increases
          const return1 =
            (exitPrice1 - position.entryPrice1) / position.entryPrice1;
          const return2 =
            (exitPrice2 - position.entryPrice2) / position.entryPrice2;
          tradeReturn = return1 - return2;
        } else {
          // Short spread: profit when spread decreases
          const return1 =
            (position.entryPrice1 - exitPrice1) / position.entryPrice1;
          const return2 =
            (exitPrice2 - position.entryPrice2) / position.entryPrice2;
          tradeReturn = return1 - return2;
        }

        // Apply transaction costs
        tradeReturn -= settings.transactionCost * 2; // Entry + Exit

        const trade = {
          entryDate: position.entryDate,
          entryTime: new Date(
            priceData1[position.entry]?.timestamp
          ).toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          }),
          exitDate: currentDate,
          exitTime: new Date(priceData1[i]?.timestamp).toLocaleTimeString(
            "en-US",
            {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
            }
          ),
          type: position.type,
          typeDescription:
            position.type === "long"
              ? `Long Asset1, Short Asset2`
              : `Short Asset1, Long Asset2`,
          entryZScore: position.entryZScore,
          exitZScore: currentZScore,
          return: tradeReturn,
          duration: i - position.entry,
          exitReason,
        };

        trades.push(trade);
        totalReturn += tradeReturn;

        if (tradeReturn > 0) wins++;
        else losses++;

        position = null;
      }
    }

    // Update equity curve
    const currentEquity = 1 + totalReturn;
    if (currentEquity > peakValue) {
      peakValue = currentEquity;
    }
    const currentDrawdown = (peakValue - currentEquity) / peakValue;
    maxDrawdown = Math.max(maxDrawdown, currentDrawdown);

    equityCurve.push({
      date: priceData1[i]?.timestamp,
      value: currentEquity,
      drawdown: currentDrawdown,
    });

    if (i > 0) {
      const dayReturn =
        currentEquity / equityCurve[equityCurve.length - 2].value - 1;
      dailyReturns.push(dayReturn);
    }
  }

  // Calculate performance metrics
  const winRate = trades.length > 0 ? (wins / trades.length) * 100 : 0;
  const avgWin =
    wins > 0
      ? trades
          .filter((t) => t.return > 0)
          .reduce((sum, t) => sum + t.return, 0) / wins
      : 0;
  const avgLoss =
    losses > 0
      ? trades
          .filter((t) => t.return < 0)
          .reduce((sum, t) => sum + t.return, 0) / losses
      : 0;
  const profitFactor =
    avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : avgWin > 0 ? Infinity : 0;

  // Sharpe ratio (assuming 252 trading days)
  const avgDailyReturn =
    dailyReturns.reduce((sum, r) => sum + r, 0) / dailyReturns.length;
  const dailyStd = Math.sqrt(
    dailyReturns.reduce((sum, r) => sum + Math.pow(r - avgDailyReturn, 2), 0) /
      dailyReturns.length
  );
  const sharpeRatio =
    dailyStd > 0 ? (avgDailyReturn / dailyStd) * Math.sqrt(252) : 0;

  return {
    trades,
    totalReturn: totalReturn * 100, // Convert to percentage
    winRate,
    profitFactor,
    sharpeRatio,
    maxDrawdown: maxDrawdown * 100,
    avgWin: avgWin * 100,
    avgLoss: avgLoss * 100,
    totalTrades: trades.length,
    equityCurve,
    dailyReturns: dailyReturns.map((r) => r * 100),
    summary: {
      wins,
      losses,
      avgTradeDuration:
        trades.length > 0
          ? trades.reduce((sum, t) => sum + t.duration, 0) / trades.length
          : 0,
    },
  };
};

// Enhanced Symbol Search Component
const SymbolSearchInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  filteredSymbols,
  showDropdown,
  onSelect,
  dropdownStyle = {},
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);

  const handleScroll = (e) => {
    e.stopPropagation();
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          background: BINANCE_COLORS.primary,
          border: `2px solid ${
            isHovered ? BINANCE_COLORS.accent : BINANCE_COLORS.gray
          }`,
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "500",
          color: BINANCE_COLORS.textPrimary,
          padding: "12px 16px",
          width: "100%",
          transition: "all 0.2s ease",
          outline: "none",
        }}
      />
      {showDropdown && (
        <div
          ref={dropdownRef}
          onScroll={handleScroll}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: BINANCE_COLORS.secondary,
            border: `1px solid ${BINANCE_COLORS.gray}`,
            borderRadius: "8px",
            maxHeight: "300px",
            overflowY: "auto",
            zIndex: 2000,
            boxShadow: "0 -10px 25px -5px rgba(0, 0, 0, 0.5)",
            ...dropdownStyle,
          }}
        >
          {filteredSymbols.map((symbol) => (
            <div
              key={symbol.symbol}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                borderBottom: `1px solid ${BINANCE_COLORS.gray}`,
                display: "flex",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                color: BINANCE_COLORS.textPrimary,
              }}
              onMouseEnter={(e) =>
                (e.target.style.background = `${BINANCE_COLORS.blue}20`)
              }
              onMouseLeave={(e) => (e.target.style.background = "transparent")}
              onClick={() => onSelect(symbol.symbol)}
            >
              <span style={{ fontWeight: "600" }}>{symbol.symbol}</span>
              <span
                style={{
                  fontSize: "12px",
                  color: BINANCE_COLORS.textSecondary,
                }}
              >
                {symbol.baseAsset}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Enhanced Backtest Settings Panel with Optimization
const BacktestSettingsPanel = ({
  settings,
  onSettingsChange,
  onRunBacktest,
  isBacktesting,
  onOptimize,
  isOptimizing,
  optimizationResults,
}) => {
  return (
    <div
      style={{
        background: BINANCE_COLORS.secondary,
        border: `1px solid ${BINANCE_COLORS.gray}`,
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "24px",
      }}
    >
      <h4
        style={{
          fontSize: "16px",
          fontWeight: "600",
          color: BINANCE_COLORS.textPrimary,
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <BarChart3 size={18} color={BINANCE_COLORS.accent} />
        Backtest Settings
      </h4>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <div>
          <label
            style={{
              fontSize: "12px",
              color: BINANCE_COLORS.textSecondary,
              display: "block",
              marginBottom: "8px",
            }}
          >
            Entry Threshold (Z-Score)
          </label>
          <input
            type="number"
            step="0.1"
            min="0.5"
            max="5"
            value={settings.entryThreshold}
            onChange={(e) =>
              onSettingsChange({
                ...settings,
                entryThreshold: parseFloat(e.target.value),
              })
            }
            style={{
              background: BINANCE_COLORS.primary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "4px",
              color: BINANCE_COLORS.textPrimary,
              padding: "8px 12px",
              width: "100%",
              fontSize: "14px",
            }}
          />
        </div>

        <div>
          <label
            style={{
              fontSize: "12px",
              color: BINANCE_COLORS.textSecondary,
              display: "block",
              marginBottom: "8px",
            }}
          >
            Exit Threshold (Z-Score)
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={settings.exitThreshold}
            onChange={(e) =>
              onSettingsChange({
                ...settings,
                exitThreshold: parseFloat(e.target.value),
              })
            }
            style={{
              background: BINANCE_COLORS.primary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "4px",
              color: BINANCE_COLORS.textPrimary,
              padding: "8px 12px",
              width: "100%",
              fontSize: "14px",
            }}
          />
        </div>

        <div>
          <label
            style={{
              fontSize: "12px",
              color: BINANCE_COLORS.textSecondary,
              display: "block",
              marginBottom: "8px",
            }}
          >
            Stop Loss (Z-Score)
          </label>
          <input
            type="number"
            step="0.1"
            min="2"
            max="10"
            value={settings.stopLoss}
            onChange={(e) =>
              onSettingsChange({
                ...settings,
                stopLoss: parseFloat(e.target.value),
              })
            }
            style={{
              background: BINANCE_COLORS.primary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "4px",
              color: BINANCE_COLORS.textPrimary,
              padding: "8px 12px",
              width: "100%",
              fontSize: "14px",
            }}
          />
        </div>

        <div>
          <label
            style={{
              fontSize: "12px",
              color: BINANCE_COLORS.textSecondary,
              display: "block",
              marginBottom: "8px",
            }}
          >
            Transaction Cost (%)
          </label>
          <input
            type="number"
            step="0.001"
            min="0"
            max="0.01"
            value={settings.transactionCost}
            onChange={(e) =>
              onSettingsChange({
                ...settings,
                transactionCost: parseFloat(e.target.value),
              })
            }
            style={{
              background: BINANCE_COLORS.primary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "4px",
              color: BINANCE_COLORS.textPrimary,
              padding: "8px 12px",
              width: "100%",
              fontSize: "14px",
            }}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <button
          onClick={onRunBacktest}
          disabled={isBacktesting}
          style={{
            background: `linear-gradient(135deg, ${BINANCE_COLORS.green} 0%, #018A5B 100%)`,
            border: "none",
            borderRadius: "6px",
            color: "white",
            padding: "10px 20px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: isBacktesting ? "not-allowed" : "pointer",
            opacity: isBacktesting ? 0.6 : 1,
          }}
        >
          {isBacktesting ? "Running Backtest..." : "Run Backtest"}
        </button>

        <button
          onClick={onOptimize}
          disabled={isOptimizing}
          style={{
            background: `linear-gradient(135deg, ${BINANCE_COLORS.purple} 0%, #9A7CED 100%)`,
            border: "none",
            borderRadius: "6px",
            color: "white",
            padding: "10px 20px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: isOptimizing ? "not-allowed" : "pointer",
            opacity: isOptimizing ? 0.6 : 1,
          }}
        >
          {isOptimizing ? "Optimizing..." : "Optimize Parameters"}
        </button>
      </div>

      {optimizationResults && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: `${BINANCE_COLORS.purple}10`,
            border: `1px solid ${BINANCE_COLORS.purple}40`,
            borderRadius: "8px",
          }}
        >
          <h5
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: BINANCE_COLORS.purple,
              marginBottom: "12px",
            }}
          >
            Optimized Parameters (Sharpe:{" "}
            {optimizationResults.result.sharpeRatio.toFixed(2)})
          </h5>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "12px",
              fontSize: "12px",
            }}
          >
            <div>
              Entry:{" "}
              <strong style={{ color: BINANCE_COLORS.textPrimary }}>
                {optimizationResults.entryThreshold}
              </strong>
            </div>
            <div>
              Exit:{" "}
              <strong style={{ color: BINANCE_COLORS.textPrimary }}>
                {optimizationResults.exitThreshold}
              </strong>
            </div>
            <div>
              Stop:{" "}
              <strong style={{ color: BINANCE_COLORS.textPrimary }}>
                {optimizationResults.stopLoss}
              </strong>
            </div>
            <div>
              Return:{" "}
              <strong
                style={{
                  color:
                    optimizationResults.result.totalReturn >= 0
                      ? BINANCE_COLORS.green
                      : BINANCE_COLORS.red,
                }}
              >
                {optimizationResults.result.totalReturn.toFixed(2)}%
              </strong>
            </div>
          </div>
          <button
            onClick={() => onSettingsChange(optimizationResults)}
            style={{
              marginTop: "12px",
              background: `linear-gradient(135deg, ${BINANCE_COLORS.accent} 0%, #E6B800 100%)`,
              border: "none",
              borderRadius: "4px",
              color: BINANCE_COLORS.primary,
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Apply Optimized Settings
          </button>
        </div>
      )}
    </div>
  );
};

// Backtest Results Display
const BacktestResults = ({ results, symbol1, symbol2 }) => {
  if (!results) return null;

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  };

  // Prepare returns distribution data
  const distributionData = results.dailyReturns.map((ret, index) => ({
    day: index + 1,
    return: ret,
    color: ret >= 0 ? BINANCE_COLORS.green : BINANCE_COLORS.red,
  }));

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* Performance Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        {[
          {
            label: "Total Return",
            value: `${results.totalReturn.toFixed(2)}%`,
            color:
              results.totalReturn >= 0
                ? BINANCE_COLORS.green
                : BINANCE_COLORS.red,
          },
          {
            label: "Win Rate",
            value: `${results.winRate.toFixed(1)}%`,
            color: BINANCE_COLORS.blue,
          },
          {
            label: "Profit Factor",
            value: results.profitFactor.toFixed(2),
            color: BINANCE_COLORS.purple,
          },
          {
            label: "Sharpe Ratio",
            value: results.sharpeRatio.toFixed(2),
            color: BINANCE_COLORS.accent,
          },
          {
            label: "Max Drawdown",
            value: `${results.maxDrawdown.toFixed(2)}%`,
            color: BINANCE_COLORS.red,
          },
          {
            label: "Total Trades",
            value: results.totalTrades.toString(),
            color: BINANCE_COLORS.textPrimary,
          },
        ].map((metric, index) => (
          <div
            key={index}
            style={{
              background: BINANCE_COLORS.secondary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "8px",
              padding: "16px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: metric.color,
                marginBottom: "4px",
              }}
            >
              {metric.value}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: BINANCE_COLORS.textSecondary,
                textTransform: "uppercase",
                fontWeight: "600",
              }}
            >
              {metric.label}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
          marginBottom: "24px",
        }}
      >
        {/* Equity Curve */}
        <div
          style={{
            background: BINANCE_COLORS.secondary,
            border: `1px solid ${BINANCE_COLORS.gray}`,
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: BINANCE_COLORS.textPrimary,
              marginBottom: "16px",
            }}
          >
            Equity Curve
          </h4>
          <div style={{ height: "250px" }}>
            <ResponsiveContainer>
              <LineChart
                data={results.equityCurve.map((point) => ({
                  ...point,
                  date: formatDate(point.date),
                }))}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={BINANCE_COLORS.gray}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="date"
                  stroke={BINANCE_COLORS.textPrimary}
                  fontSize={10}
                  tick={{ fill: BINANCE_COLORS.textPrimary }}
                />
                <YAxis
                  stroke={BINANCE_COLORS.textPrimary}
                  fontSize={10}
                  tick={{ fill: BINANCE_COLORS.textPrimary }}
                  tickFormatter={(value) =>
                    `${((value - 1) * 100).toFixed(1)}%`
                  }
                />
                <Tooltip
                  formatter={(value) => [
                    `${((value - 1) * 100).toFixed(2)}%`,
                    "Return",
                  ]}
                  contentStyle={{
                    background: BINANCE_COLORS.secondary,
                    border: `1px solid ${BINANCE_COLORS.gray}`,
                    borderRadius: "8px",
                    color: BINANCE_COLORS.textPrimary,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={BINANCE_COLORS.green}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Returns Distribution */}
        <div
          style={{
            background: BINANCE_COLORS.secondary,
            border: `1px solid ${BINANCE_COLORS.gray}`,
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: BINANCE_COLORS.textPrimary,
              marginBottom: "16px",
            }}
          >
            Daily Returns Distribution
          </h4>
          <div style={{ height: "250px" }}>
            <ResponsiveContainer>
              <BarChart data={distributionData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={BINANCE_COLORS.gray}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="day"
                  stroke={BINANCE_COLORS.textPrimary}
                  fontSize={10}
                  tick={{ fill: BINANCE_COLORS.textPrimary }}
                />
                <YAxis
                  stroke={BINANCE_COLORS.textPrimary}
                  fontSize={10}
                  tick={{ fill: BINANCE_COLORS.textPrimary }}
                  tickFormatter={(value) => `${value.toFixed(1)}%`}
                />
                <Tooltip
                  formatter={(value) => [`${value.toFixed(2)}%`, "Return"]}
                  contentStyle={{
                    background: BINANCE_COLORS.secondary,
                    border: `1px solid ${BINANCE_COLORS.gray}`,
                    borderRadius: "8px",
                    color: BINANCE_COLORS.textPrimary,
                  }}
                />
                <Bar dataKey="return">
                  {distributionData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Trade History Table */}
      <div
        style={{
          background: BINANCE_COLORS.secondary,
          border: `1px solid ${BINANCE_COLORS.gray}`,
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <h4
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: BINANCE_COLORS.textPrimary,
            marginBottom: "16px",
          }}
        >
          All Trades ({results.trades.length} total)
        </h4>
        <div
          style={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "400px", // Add vertical scrolling with max height
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: BINANCE_COLORS.secondary,
                zIndex: 1,
              }}
            >
              <tr style={{ borderBottom: `1px solid ${BINANCE_COLORS.gray}` }}>
                {[
                  "Entry Date",
                  "Entry Time",
                  "Exit Date",
                  "Exit Time",
                  "Type",
                  "Entry Z-Score",
                  "Exit Z-Score",
                  "Return (%)",
                  "Duration",
                  "Reason",
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textSecondary,
                      fontWeight: "600",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.trades.map((trade, index) => (
                <tr
                  key={index}
                  style={{ borderBottom: `1px solid ${BINANCE_COLORS.gray}40` }}
                >
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.entryDate}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.entryTime}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.exitDate}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.exitTime}
                  </td>
                  <td style={{ padding: "12px 8px", fontSize: "12px" }}>
                    <div title={trade.typeDescription}>
                      <span
                        style={{
                          color:
                            trade.type === "long"
                              ? BINANCE_COLORS.green
                              : BINANCE_COLORS.red,
                          fontWeight: "600",
                        }}
                      >
                        {trade.type.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.entryZScore.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.exitZScore.toFixed(2)}
                  </td>
                  <td style={{ padding: "12px 8px", fontSize: "12px" }}>
                    <span
                      style={{
                        color:
                          trade.return >= 0
                            ? BINANCE_COLORS.green
                            : BINANCE_COLORS.red,
                        fontWeight: "600",
                      }}
                    >
                      {(trade.return * 100).toFixed(2)}%
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textPrimary,
                    }}
                  >
                    {trade.duration} periods
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      fontSize: "12px",
                      color: BINANCE_COLORS.textSecondary,
                    }}
                  >
                    {trade.exitReason}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Enhanced Copula Analysis Component
const CopulaAnalysis = ({ data, symbol1, symbol2 }) => {
  return (
    <div
      style={{
        background: BINANCE_COLORS.secondary,
        border: `1px solid ${BINANCE_COLORS.gray}`,
        borderRadius: "8px",
        padding: "20px",
      }}
    >
      <h4
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: BINANCE_COLORS.textPrimary,
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Target size={16} color={BINANCE_COLORS.accent} />
        Copula Analysis
      </h4>

      <div style={{ height: "160px", marginBottom: "16px" }}>
        <ResponsiveContainer>
          <ScatterChart data={data.copulaPoints || []}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={BINANCE_COLORS.gray}
              opacity={0.3}
            />
            <XAxis
              type="number"
              dataKey="u1"
              domain={[0, 1]}
              stroke={BINANCE_COLORS.textPrimary}
              fontSize={10}
              tick={{ fill: BINANCE_COLORS.textPrimary }}
            />
            <YAxis
              type="number"
              dataKey="u2"
              domain={[0, 1]}
              stroke={BINANCE_COLORS.textPrimary}
              fontSize={10}
              tick={{ fill: BINANCE_COLORS.textPrimary }}
            />
            <Tooltip
              formatter={(value, name) => [
                value.toFixed(3),
                name === "u1"
                  ? `${symbol1} Rank`
                  : name === "u2"
                  ? `${symbol2} Rank`
                  : "Density",
              ]}
              labelFormatter={() => "Copula Space"}
              contentStyle={{
                background: BINANCE_COLORS.secondary,
                border: `1px solid ${BINANCE_COLORS.gray}`,
                borderRadius: "8px",
                color: BINANCE_COLORS.textPrimary,
              }}
            />
            <Scatter dataKey="density" fill={BINANCE_COLORS.purple}>
              {(data.copulaPoints || []).map((entry, index) => (
                <Cell
                  key={index}
                  fill={`${BINANCE_COLORS.purple}${Math.floor(
                    entry.density * 255
                  )
                    .toString(16)
                    .padStart(2, "0")}`}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          fontSize: "12px",
        }}
      >
        <div>
          <div
            style={{ color: BINANCE_COLORS.textSecondary, marginBottom: "4px" }}
          >
            Copula Type
          </div>
          <div style={{ color: BINANCE_COLORS.textPrimary, fontWeight: "600" }}>
            {data.copulaType || "Gaussian"}
          </div>
          <div
            style={{
              color: BINANCE_COLORS.textSecondary,
              marginTop: "8px",
              marginBottom: "4px",
            }}
          >
            Kendall's τ
          </div>
          <div style={{ color: BINANCE_COLORS.purple, fontWeight: "600" }}>
            {((data.dependence || 0) * 100).toFixed(1)}%
          </div>
        </div>
        <div>
          <div
            style={{ color: BINANCE_COLORS.textSecondary, marginBottom: "4px" }}
          >
            Tail Dependence
          </div>
          <div style={{ color: BINANCE_COLORS.blue, fontWeight: "600" }}>
            {((data.tailDependence || 0) * 100).toFixed(1)}%
          </div>
          <div
            style={{
              color: BINANCE_COLORS.textSecondary,
              marginTop: "8px",
              marginBottom: "4px",
            }}
          >
            Joint Risk
          </div>
          <div style={{ color: BINANCE_COLORS.red, fontWeight: "600" }}>
            {data.dependence > 0.5
              ? "High"
              : data.dependence > 0.2
              ? "Medium"
              : "Low"}
          </div>
        </div>
      </div>
    </div>
  );
};

// Asset Info Header
const AssetInfoHeader = ({
  symbol1,
  symbol2,
  tickerData1,
  tickerData2,
  realTimeMetrics,
}) => {
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${BINANCE_COLORS.primary} 0%, ${BINANCE_COLORS.secondary} 100%)`,
        border: `1px solid ${BINANCE_COLORS.gray}`,
        borderRadius: "8px",
        padding: "24px",
        marginBottom: "24px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto 1fr",
          gap: "32px",
          alignItems: "center",
        }}
      >
        {/* Asset 1 Info */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              width: "8px",
              height: "40px",
              backgroundColor: BINANCE_COLORS.blue,
              borderRadius: "4px",
            }}
          />
          <div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: BINANCE_COLORS.textPrimary,
                marginBottom: "4px",
              }}
            >
              {symbol1}
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: BINANCE_COLORS.blue,
                marginBottom: "2px",
              }}
            >
              $
              {tickerData1
                ? parseFloat(tickerData1.lastPrice).toFixed(4)
                : "0.0000"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color:
                  tickerData1 && parseFloat(tickerData1.priceChangePercent) >= 0
                    ? BINANCE_COLORS.green
                    : BINANCE_COLORS.red,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {tickerData1 && parseFloat(tickerData1.priceChangePercent) >= 0
                ? "↗"
                : "↘"}
              {tickerData1
                ? Math.abs(parseFloat(tickerData1.priceChangePercent)).toFixed(
                    2
                  )
                : "0.00"}
              %
              <span
                style={{
                  color: BINANCE_COLORS.textSecondary,
                  marginLeft: "8px",
                }}
              >
                Vol:{" "}
                {tickerData1
                  ? (parseFloat(tickerData1.volume) / 1000000).toFixed(1) + "M"
                  : "0M"}
              </span>
            </div>
          </div>
        </div>

        {/* Asset 2 Info */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              width: "8px",
              height: "40px",
              backgroundColor: BINANCE_COLORS.red,
              borderRadius: "4px",
            }}
          />
          <div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: BINANCE_COLORS.textPrimary,
                marginBottom: "4px",
              }}
            >
              {symbol2}
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: BINANCE_COLORS.red,
                marginBottom: "2px",
              }}
            >
              $
              {tickerData2
                ? parseFloat(tickerData2.lastPrice).toFixed(4)
                : "0.0000"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color:
                  tickerData2 && parseFloat(tickerData2.priceChangePercent) >= 0
                    ? BINANCE_COLORS.green
                    : BINANCE_COLORS.red,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {tickerData2 && parseFloat(tickerData2.priceChangePercent) >= 0
                ? "↗"
                : "↘"}
              {tickerData2
                ? Math.abs(parseFloat(tickerData2.priceChangePercent)).toFixed(
                    2
                  )
                : "0.00"}
              %
              <span
                style={{
                  color: BINANCE_COLORS.textSecondary,
                  marginLeft: "8px",
                }}
              >
                Vol:{" "}
                {tickerData2
                  ? (parseFloat(tickerData2.volume) / 1000000).toFixed(1) + "M"
                  : "0M"}
              </span>
            </div>
          </div>
        </div>

        {/* Real-time Performance Metrics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "24px",
            padding: "16px",
            background: "rgba(252, 213, 53, 0.05)",
            borderRadius: "8px",
            border: `1px solid ${BINANCE_COLORS.accent}33`,
          }}
        >
          {[
            {
              label: "Correlation",
              value: (realTimeMetrics.correlation * 100).toFixed(1) + "%",
              color: BINANCE_COLORS.green,
            },
            {
              label: "Z-Score",
              value: realTimeMetrics.zscore.toFixed(2),
              color: BINANCE_COLORS.blue,
            },
            {
              label: "Beta",
              value: realTimeMetrics.beta.toFixed(2),
              color: BINANCE_COLORS.purple,
            },
            {
              label: "Spread",
              value: realTimeMetrics.spread.toFixed(4),
              color: BINANCE_COLORS.accent,
            },
            {
              label: "Signal",
              value: realTimeMetrics.signal,
              color:
                realTimeMetrics.signal === "BUY"
                  ? BINANCE_COLORS.green
                  : realTimeMetrics.signal === "SELL"
                  ? BINANCE_COLORS.red
                  : BINANCE_COLORS.textSecondary,
            },
          ].map((metric, index) => (
            <div key={index} style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: metric.color,
                  marginBottom: "4px",
                }}
              >
                {metric.value}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: BINANCE_COLORS.textSecondary,
                  textTransform: "uppercase",
                  fontWeight: "600",
                }}
              >
                {metric.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Cumulative Returns Chart with Zoom
const CumulativeReturnsChart = ({ data, symbol1, symbol2 }) => {
  const [zoomDomain, setZoomDomain] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const resetZoom = () => {
    setZoomDomain(null);
    setIsZoomed(false);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: BINANCE_COLORS.secondary,
            border: `1px solid ${BINANCE_COLORS.gray}`,
            borderRadius: "8px",
            padding: "12px",
            color: BINANCE_COLORS.textPrimary,
            fontSize: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
          }}
        >
          <p style={{ margin: "0 0 8px 0", fontWeight: "600" }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: "4px 0", color: entry.color }}>
              {`${entry.dataKey === "return1" ? symbol1 : symbol2}: ${(
                entry.value * 100
              ).toFixed(2)}%`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: "100%", height: "300px", position: "relative" }}>
      {isZoomed && (
        <button
          onClick={resetZoom}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: BINANCE_COLORS.accent,
            border: "none",
            borderRadius: "4px",
            color: BINANCE_COLORS.primary,
            padding: "4px 8px",
            fontSize: "10px",
            fontWeight: "600",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          Reset Zoom
        </button>
      )}
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={BINANCE_COLORS.gray}
            opacity={0.3}
          />
          <XAxis
            dataKey="date"
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
            domain={
              zoomDomain
                ? [zoomDomain.left, zoomDomain.right]
                : ["dataMin", "dataMax"]
            }
          />
          <YAxis
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
            tickFormatter={(value) => `${(value * 100).toFixed(1)}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="return1"
            stroke={BINANCE_COLORS.blue}
            strokeWidth={2}
            dot={false}
            name={symbol1}
          />
          <Line
            type="monotone"
            dataKey="return2"
            stroke={BINANCE_COLORS.red}
            strokeWidth={2}
            dot={false}
            name={symbol2}
          />
          <ReferenceLine
            y={0}
            stroke={BINANCE_COLORS.textSecondary}
            strokeDasharray="2 2"
            opacity={0.5}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Spread & Z-Score Chart with Zoom
const SpreadZScoreChart = ({ data, currentZScore }) => {
  const [zoomDomain, setZoomDomain] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const resetZoom = () => {
    setZoomDomain(null);
    setIsZoomed(false);
  };

  return (
    <div style={{ width: "100%", height: "300px", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          display: "flex",
          gap: "8px",
          zIndex: 10,
        }}
      >
        <div
          style={{
            background: `${BINANCE_COLORS.green}20`,
            border: `1px solid ${BINANCE_COLORS.green}`,
            borderRadius: "8px",
            padding: "8px 12px",
          }}
        >
          <div
            style={{
              fontSize: "10px",
              color: BINANCE_COLORS.textSecondary,
              marginBottom: "2px",
            }}
          >
            Z-SCORE
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: BINANCE_COLORS.green,
            }}
          >
            {currentZScore.toFixed(2)}
          </div>
        </div>
        {isZoomed && (
          <button
            onClick={resetZoom}
            style={{
              background: BINANCE_COLORS.accent,
              border: "none",
              borderRadius: "4px",
              color: BINANCE_COLORS.primary,
              padding: "4px 8px",
              fontSize: "10px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Reset Zoom
          </button>
        )}
      </div>

      <ResponsiveContainer>
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={BINANCE_COLORS.gray}
            opacity={0.3}
          />
          <XAxis
            dataKey="date"
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
            domain={
              zoomDomain
                ? [zoomDomain.left, zoomDomain.right]
                : ["dataMin", "dataMax"]
            }
          />
          <YAxis
            yAxisId="zscore"
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
          />
          <YAxis
            yAxisId="spread"
            orientation="right"
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
          />
          <Tooltip
            contentStyle={{
              background: BINANCE_COLORS.secondary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "8px",
              color: BINANCE_COLORS.textPrimary,
            }}
            formatter={(value, name) => [
              name === "zscore"
                ? `Z-Score: ${value.toFixed(3)}`
                : `Spread: ${value.toFixed(6)}`,
              "",
            ]}
          />
          <Line
            yAxisId="zscore"
            type="monotone"
            dataKey="zscore"
            stroke={BINANCE_COLORS.green}
            strokeWidth={2}
            dot={false}
            name="zscore"
          />
          <Line
            yAxisId="spread"
            type="monotone"
            dataKey="spread"
            stroke={BINANCE_COLORS.blue}
            strokeWidth={1}
            dot={false}
            opacity={0.7}
            name="spread"
          />
          <ReferenceLine
            yAxisId="zscore"
            y={2}
            stroke={BINANCE_COLORS.red}
            strokeDasharray="3 3"
          />
          <ReferenceLine
            yAxisId="zscore"
            y={-2}
            stroke={BINANCE_COLORS.red}
            strokeDasharray="3 3"
          />
          <ReferenceLine
            yAxisId="zscore"
            y={0}
            stroke={BINANCE_COLORS.textSecondary}
            strokeDasharray="2 2"
            opacity={0.5}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// Enhanced Statistical Metrics Panel
const StatisticalMetricsPanel = ({
  realTimeData,
  symbol1,
  symbol2,
  priceData1,
  priceData2,
}) => {
  const [advancedMetrics, setAdvancedMetrics] = useState({
    halfLife: 0,
    hurstExponent: 0.5,
    cointegration: { isCointegrated: false, pValue: 1, testStatistic: 0 },
  });

  useEffect(() => {
    if (priceData1.length > 50 && priceData2.length > 50) {
      const minLength = Math.min(priceData1.length, priceData2.length);
      const prices1 = priceData1.slice(-minLength).map((k) => k.close);
      const prices2 = priceData2.slice(-minLength).map((k) => k.close);

      // Calculate spreads for advanced metrics
      const spreads = [];
      for (let i = 1; i < minLength; i++) {
        const return1 = (prices1[i] - prices1[i - 1]) / prices1[i - 1];
        const return2 = (prices2[i] - prices2[i - 1]) / prices2[i - 1];
        spreads.push(return1 - return2);
      }

      const halfLife = calculateHalfLife(spreads);
      const hurstExponent = calculateHurstExponent(spreads);
      const cointegration = calculateCointegration(prices1, prices2);

      setAdvancedMetrics({
        halfLife,
        hurstExponent,
        cointegration,
      });
    }
  }, [priceData1, priceData2]);

  return (
    <div
      style={{
        background: BINANCE_COLORS.secondary,
        border: `1px solid ${BINANCE_COLORS.gray}`,
        borderRadius: "8px",
        padding: "20px",
        height: "400px",
      }}
    >
      <h4
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: BINANCE_COLORS.textPrimary,
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Activity size={16} color={BINANCE_COLORS.accent} />
        Statistical Metrics
      </h4>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Correlation Metrics */}
        {[
          {
            label: "Current Correlation",
            value: realTimeData.correlation,
            color: BINANCE_COLORS.blue,
            format: "percent",
          },
          {
            label: "20-Period Rolling",
            value: realTimeData.rolling20Correlation,
            color: BINANCE_COLORS.green,
            format: "percent",
          },
          {
            label: "50-Period Rolling",
            value: realTimeData.rolling50Correlation,
            color: BINANCE_COLORS.accent,
            format: "percent",
          },
          {
            label: "Beta Coefficient",
            value: realTimeData.beta,
            color: BINANCE_COLORS.purple,
            format: "number",
          },
        ].map((item, index) => (
          <div key={index}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  color: BINANCE_COLORS.textSecondary,
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: item.color,
                }}
              >
                {item.format === "percent"
                  ? (item.value * 100).toFixed(1) + "%"
                  : item.value.toFixed(2)}
              </span>
            </div>
            <div
              style={{
                width: "100%",
                height: "4px",
                background: BINANCE_COLORS.primary,
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(
                    Math.abs(item.value) *
                      (item.format === "percent"
                        ? 100
                        : item.format === "number"
                        ? 50
                        : 100),
                    100
                  )}%`,
                  height: "100%",
                  background: item.color,
                  borderRadius: "2px",
                }}
              />
            </div>
          </div>
        ))}

        {/* Advanced Metrics */}
        <div
          style={{
            borderTop: `1px solid ${BINANCE_COLORS.gray}40`,
            paddingTop: "16px",
            marginTop: "8px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              fontSize: "12px",
            }}
          >
            <div>
              <div
                style={{
                  color: BINANCE_COLORS.textSecondary,
                  marginBottom: "4px",
                }}
              >
                Half-Life
              </div>
              <div style={{ color: BINANCE_COLORS.orange, fontWeight: "600" }}>
                {advancedMetrics.halfLife > 0
                  ? advancedMetrics.halfLife.toFixed(1) + " periods"
                  : "N/A"}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: BINANCE_COLORS.textSecondary,
                  marginBottom: "4px",
                }}
              >
                Hurst Exponent
              </div>
              <div
                style={{
                  color:
                    advancedMetrics.hurstExponent < 0.5
                      ? BINANCE_COLORS.green
                      : advancedMetrics.hurstExponent > 0.5
                      ? BINANCE_COLORS.red
                      : BINANCE_COLORS.textPrimary,
                  fontWeight: "600",
                }}
              >
                {advancedMetrics.hurstExponent.toFixed(3)}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: BINANCE_COLORS.textSecondary,
                  marginBottom: "4px",
                }}
              >
                Cointegrated
              </div>
              <div
                style={{
                  color: advancedMetrics.cointegration.isCointegrated
                    ? BINANCE_COLORS.green
                    : BINANCE_COLORS.red,
                  fontWeight: "600",
                }}
              >
                {advancedMetrics.cointegration.isCointegrated ? "Yes" : "No"}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: BINANCE_COLORS.textSecondary,
                  marginBottom: "4px",
                }}
              >
                P-Value
              </div>
              <div
                style={{ color: BINANCE_COLORS.textPrimary, fontWeight: "600" }}
              >
                {advancedMetrics.cointegration.pValue.toFixed(3)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dynamic Correlation Chart with Zoom
const DynamicCorrelationChart = ({ data }) => {
  const [zoomDomain, setZoomDomain] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const resetZoom = () => {
    setZoomDomain(null);
    setIsZoomed(false);
  };

  return (
    <div style={{ width: "100%", height: "280px", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          display: "flex",
          gap: "8px",
          zIndex: 10,
        }}
      >
        <div
          style={{
            background: `${BINANCE_COLORS.purple}20`,
            border: `1px solid ${BINANCE_COLORS.purple}`,
            borderRadius: "8px",
            padding: "8px 12px",
          }}
        >
          <div
            style={{
              fontSize: "10px",
              color: BINANCE_COLORS.textSecondary,
              marginBottom: "2px",
            }}
          >
            CORRELATION
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: BINANCE_COLORS.purple,
            }}
          >
            {data.length > 0
              ? (data[data.length - 1]?.correlation * 100).toFixed(1) + "%"
              : "0%"}
          </div>
        </div>
        {isZoomed && (
          <button
            onClick={resetZoom}
            style={{
              background: BINANCE_COLORS.accent,
              border: "none",
              borderRadius: "4px",
              color: BINANCE_COLORS.primary,
              padding: "4px 8px",
              fontSize: "10px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Reset Zoom
          </button>
        )}
      </div>

      <ResponsiveContainer>
        <AreaChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={BINANCE_COLORS.gray}
            opacity={0.3}
          />
          <XAxis
            dataKey="date"
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
            domain={
              zoomDomain
                ? [zoomDomain.left, zoomDomain.right]
                : ["dataMin", "dataMax"]
            }
          />
          <YAxis
            stroke={BINANCE_COLORS.textPrimary}
            fontSize={10}
            tick={{ fill: BINANCE_COLORS.textPrimary }}
            domain={[-1, 1]}
            tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
          />
          <Tooltip
            contentStyle={{
              background: BINANCE_COLORS.secondary,
              border: `1px solid ${BINANCE_COLORS.gray}`,
              borderRadius: "8px",
              color: BINANCE_COLORS.textPrimary,
            }}
            formatter={(value) => [
              `${(value * 100).toFixed(2)}%`,
              "Correlation",
            ]}
          />
          <Area
            type="monotone"
            dataKey="correlation"
            stroke={BINANCE_COLORS.purple}
            fill={BINANCE_COLORS.purple}
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <ReferenceLine
            y={0}
            stroke={BINANCE_COLORS.textSecondary}
            strokeDasharray="2 2"
            opacity={0.5}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main PairTrading Component
const PairTrading = ({ priceAnalyzer, showToast }) => {
  const [selectedSymbol1, setSelectedSymbol1] = useState("ALICEUSDT");
  const [selectedSymbol2, setSelectedSymbol2] = useState("YFIUSDT");
  const [selectedDays, setSelectedDays] = useState("5m");
  const [lookbackPeriod, setLookbackPeriod] = useState(20);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filteredSymbols1, setFilteredSymbols1] = useState([]);
  const [filteredSymbols2, setFilteredSymbols2] = useState([]);
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  const [searchSymbol1, setSearchSymbol1] = useState("ALICEUSDT");
  const [searchSymbol2, setSearchSymbol2] = useState("YFIUSDT");

  // Real-time data states
  const [tickerData1, setTickerData1] = useState(null);
  const [tickerData2, setTickerData2] = useState(null);
  const [priceData1, setPriceData1] = useState([]);
  const [priceData2, setPriceData2] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [copulaData, setCopulaData] = useState({
    copulaPoints: [],
    dependence: 0,
  });
  const [realTimeMetrics, setRealTimeMetrics] = useState({
    correlation: 0,
    rolling20Correlation: 0,
    rolling50Correlation: 0,
    zscore: 0,
    beta: 1,
    spread: 0,
    signal: "HOLD",
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const updateIntervalRef = useRef(null);

  // Backtesting states
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestSettings, setBacktestSettings] = useState({
    entryThreshold: 2.0,
    exitThreshold: 0.5,
    stopLoss: 3.0,
    takeProfit: 3.0,
    transactionCost: 0.0004, // 0.1%
  });
  const [isBacktesting, setIsBacktesting] = useState(false);
  const [showBacktestPanel, setShowBacktestPanel] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [showLiveAnalysisPopup, setShowLiveAnalysisPopup] = useState(false);
  const [tradeablePairs, setTradeablePairs] = useState([]);
  const [selectedBaseCoin, setSelectedBaseCoin] = useState("");
  const [analyzingPairs, setAnalyzingPairs] = useState(false);
  const [searchBaseCoin, setSearchBaseCoin] = useState("");
  const [filteredBaseCoins, setFilteredBaseCoins] = useState([]);
  const [showBaseCoinDropdown, setShowBaseCoinDropdown] = useState(false);
  const calculateZScore2 = useCallback(async (symbol, days = 30) => {
    try {
      console.log(`🔍 Calculating Z-Score for ${symbol}...`);
      const klineData = await fetchBinanceKlines(symbol, selectedDays, 1000);

      if (!klineData || !klineData.length) {
        console.log(`❌ No data for ${symbol}`);
        return null;
      }

      const prices = klineData.map((k) => k.close);
      const mean =
        prices.reduce((sum, price) => sum + price, 0) / prices.length;
      const variance =
        prices.reduce((sum, price) => sum + Math.pow(price - mean, 2), 0) /
        prices.length;
      const stdDev = Math.sqrt(variance);

      if (stdDev === 0) return null;

      const currentPrice = prices[prices.length - 1];
      const zScore = (currentPrice - mean) / stdDev;

      console.log(`✅ Z-Score for ${symbol}: ${Math.abs(zScore).toFixed(2)}`);
      return Math.abs(zScore);
    } catch (error) {
      console.error(`❌ Error calculating Z-Score for ${symbol}:`, error);
      return null;
    }
  }, []);
  const findTradeablePairs = useCallback(
    async (baseCoin) => {
      console.log(`🚀 Starting analysis for base coin: ${baseCoin}`);
      setAnalyzingPairs(true);
      setTradeablePairs([]); // Clear previous results

      try {
        const availableSymbols = priceAnalyzer.symbols || [];
        console.log(`📊 Total available symbols: ${availableSymbols.length}`);

        // For pairs trading, we want to find OTHER coins to pair with the selected coin
        // Get a diverse set of potential pairs, excluding the selected coin itself
        const potentialPairs = availableSymbols.filter((s) => {
          // Exclude the selected coin itself
          if (s.baseAsset === baseCoin) return false;

          // Only include USDT pairs for consistency
          if (!s.symbol.endsWith("USDT")) return false;

          return true;
        });

        console.log(
          `🔗 Found ${potentialPairs.length} potential pairs to analyze with ${baseCoin}`
        );
        console.log(
          `📝 Symbols to analyze:`,
          potentialPairs.map((s) => s.symbol)
        );

        if (potentialPairs.length === 0) {
          console.log(`❌ No potential pairs found for: ${baseCoin}`);
          setAnalyzingPairs(false);
          return;
        }

        const tradeable = [];

        // Analyze each potential pair
        for (let i = 0; i < potentialPairs.length; i++) {
          const symbol = potentialPairs[i];
          console.log(
            `📈 Analyzing ${i + 1}/${potentialPairs.length}: ${symbol.symbol}`
          );

          try {
            const zScore = await calculateZScore2(symbol.symbol);
            console.log(`📊 ${symbol.symbol} Z-Score: ${zScore}`);

            // Look for coins with Z-score around 2 (good for mean reversion trading)
            if (zScore && zScore >= 1.5 && zScore <= 3.0) {
              const pair = {
                ...symbol,
                zScore: zScore.toFixed(2),
                tradeability:
                  zScore >= 2 ? "High" : zScore >= 1.8 ? "Medium" : "Low",
                pairsWith: baseCoin, // Indicate this pairs with the selected coin
                pairSymbol: `${baseCoin}/${symbol.baseAsset}`, // Show the pair
              };
              tradeable.push(pair);
              console.log(
                `✅ Added tradeable pair: ${
                  symbol.symbol
                } (Z-Score: ${zScore.toFixed(2)}) - pairs with ${baseCoin}`
              );
            }
          } catch (error) {
            console.error(`❌ Failed to analyze ${symbol.symbol}:`, error);
          }
        }

        console.log(
          `🎯 Found ${tradeable.length} tradeable pairs for ${baseCoin}`
        );

        // Sort by Z-Score closest to 2.0 (ideal for mean reversion)
        tradeable.sort(
          (a, b) =>
            Math.abs(2 - parseFloat(a.zScore)) -
            Math.abs(2 - parseFloat(b.zScore))
        );

        setTradeablePairs(tradeable);
      } catch (error) {
        console.error("❌ Error in findTradeablePairs:", error);
      } finally {
        setAnalyzingPairs(false);
      }
    },
    [priceAnalyzer.symbols, calculateZScore]
  );
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Real-time data processing
  const processRealTimeData = (klines1, klines2) => {
    if (!klines1.length || !klines2.length) return;

    const minLength = Math.min(klines1.length, klines2.length);
    const prices1 = klines1.slice(-minLength).map((k) => k.close);
    const prices2 = klines2.slice(-minLength).map((k) => k.close);

    // Calculate real-time metrics
    const correlation = calculateCorrelation(prices1, prices2);
    const beta = calculateBeta(prices1, prices2);

    // Calculate copula data
    const copulaResult = calculateCopulaData(prices1, prices2);
    setCopulaData(copulaResult);

    // Calculate spreads and z-score
    const spreads = [];
    const data = [];
    let baseReturn1 = 0;
    let baseReturn2 = 0;

    for (let i = 1; i < minLength; i++) {
      const return1 = (prices1[i] - prices1[i - 1]) / prices1[i - 1];
      const return2 = (prices2[i] - prices2[i - 1]) / prices2[i - 1];

      baseReturn1 += return1;
      baseReturn2 += return2;

      const spread = return1 - return2;
      spreads.push(spread);

      const date = new Date(klines1[i].timestamp).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      data.push({
        date,
        return1: baseReturn1,
        return2: baseReturn2,
        spread,
        zscore: spreads.length > 10 ? calculateZScore(spreads.slice(-20)) : 0,
        correlation:
          spreads.length > 20
            ? calculateCorrelation(
                prices1.slice(i - 20, i),
                prices2.slice(i - 20, i)
              )
            : correlation,
      });
    }

    const currentZScore =
      spreads.length > 10 ? calculateZScore(spreads.slice(-20)) : 0;
    const currentSpread = spreads[spreads.length - 1] || 0;

    // Generate trading signal
    let signal = "HOLD";
    if (currentZScore > 2) signal = "SELL";
    else if (currentZScore < -2) signal = "BUY";

    // Calculate rolling correlations
    const rolling20Correlation =
      data.length > 20
        ? calculateCorrelation(prices1.slice(-20), prices2.slice(-20))
        : correlation;
    const rolling50Correlation =
      data.length > 50
        ? calculateCorrelation(prices1.slice(-50), prices2.slice(-50))
        : correlation;

    setTimeSeriesData(data);
    setRealTimeMetrics({
      correlation,
      rolling20Correlation,
      rolling50Correlation,
      zscore: currentZScore,
      beta,
      spread: currentSpread,
      signal,
    });
  };

  // Real-time data fetching
  const fetchRealTimeData = async () => {
    if (!selectedSymbol1 || !selectedSymbol2 || !showResults) return;

    try {
      const [ticker1, ticker2, klines1, klines2] = await Promise.all([
        fetch24hrTicker(selectedSymbol1),
        fetch24hrTicker(selectedSymbol2),
        fetchBinanceKlines(selectedSymbol1, selectedDays, 1000),
        fetchBinanceKlines(selectedSymbol2, selectedDays, 1000),
      ]);

      setTickerData1(ticker1);
      setTickerData2(ticker2);
      setPriceData1(klines1);
      setPriceData2(klines2);

      processRealTimeData(klines1, klines2);
    } catch (error) {
      console.error("Error fetching real-time data:", error);
    }
  };

  // Start real-time updates
  useEffect(() => {
    if (showResults) {
      fetchRealTimeData();

      updateIntervalRef.current = setInterval(() => {
        fetchRealTimeData();
      }, 30000);

      return () => {
        if (updateIntervalRef.current) {
          clearInterval(updateIntervalRef.current);
        }
      };
    }
  }, [showResults, selectedSymbol1, selectedSymbol2]);

  // Symbol selection effects
  useEffect(() => {
    if (searchSymbol1) {
      const filtered =
        priceAnalyzer.symbols
          ?.filter(
            (s) =>
              s.symbol.toLowerCase().includes(searchSymbol1.toLowerCase()) ||
              s.baseAsset.toLowerCase().includes(searchSymbol1.toLowerCase())
          )
          .slice(0, 20) || [];
      setFilteredSymbols1(filtered);
    } else {
      const popular = [
        "ALICEUSDT",
        "YFIUSDT",
        "BTCUSDT",
        "ETHUSDT",
        "BNBUSDT",
        "SOLUSDT",
      ];
      const filtered =
        popular
          .map((symbol) =>
            priceAnalyzer.symbols?.find((s) => s.symbol === symbol)
          )
          .filter(Boolean) || [];
      setFilteredSymbols1(filtered);
    }
  }, [searchSymbol1, priceAnalyzer.symbols]);

  useEffect(() => {
    if (searchSymbol2) {
      const filtered =
        priceAnalyzer.symbols
          ?.filter(
            (s) =>
              s.symbol.toLowerCase().includes(searchSymbol2.toLowerCase()) ||
              s.baseAsset.toLowerCase().includes(searchSymbol2.toLowerCase())
          )
          .slice(0, 20) || [];
      setFilteredSymbols2(filtered);
    } else {
      const popular = [
        "YFIUSDT",
        "ALICEUSDT",
        "BTCUSDT",
        "ETHUSDT",
        "BNBUSDT",
        "SOLUSDT",
      ];
      const filtered =
        popular
          .map((symbol) =>
            priceAnalyzer.symbols?.find((s) => s.symbol === symbol)
          )
          .filter(Boolean) || [];
      setFilteredSymbols2(filtered);
    }
  }, [searchSymbol2, priceAnalyzer.symbols]);

  const handlePairAnalysis = async () => {
    if (!selectedSymbol1.trim() || !selectedSymbol2.trim()) {
      showToast("error", "Invalid Input", "Please select both trading pairs");
      return;
    }

    if (selectedSymbol1 === selectedSymbol2) {
      showToast(
        "error",
        "Invalid Pair",
        "Please select different trading pairs"
      );
      return;
    }

    setIsAnalyzing(true);
    try {
      await fetchRealTimeData();
      setShowResults(true);
      showToast(
        "success",
        "Analysis Complete",
        `Real-time analysis started for ${selectedSymbol1} vs ${selectedSymbol2}`
      );
    } catch (error) {
      showToast("error", "Analysis Failed", error.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const selectSymbol1 = (symbol) => {
    setSelectedSymbol1(symbol);
    setSearchSymbol1(symbol);
    setShowDropdown1(false);
  };

  const selectSymbol2 = (symbol) => {
    setSelectedSymbol2(symbol);
    setSearchSymbol2(symbol);
    setShowDropdown2(false);
  };

  // Backtest handlers
  const handleRunBacktest = async () => {
    if (!priceData1.length || !priceData2.length) {
      showToast(
        "error",
        "No Data",
        "Please run live analysis first to get price data"
      );
      return;
    }

    setIsBacktesting(true);
    try {
      // Use more historical data for backtesting
      const [historicalData1, historicalData2] = await Promise.all([
        fetchBinanceKlines(selectedSymbol1, selectedDays, 1000),
        fetchBinanceKlines(selectedSymbol2, selectedDays, 1000),
      ]);

      const results = runBacktest(
        historicalData1,
        historicalData2,
        backtestSettings
      );
      setBacktestResults(results);

      if (results) {
        showToast(
          "success",
          "Backtest Complete",
          `Total Return: ${results.totalReturn.toFixed(
            2
          )}%, Win Rate: ${results.winRate.toFixed(1)}%`
        );
      }
    } catch (error) {
      showToast("error", "Backtest Failed", error.message);
    } finally {
      setIsBacktesting(false);
    }
  };

  const handleOptimizeParameters = async () => {
    if (!priceData1.length || !priceData2.length) {
      showToast(
        "error",
        "No Data",
        "Please run live analysis first to get price data"
      );
      return;
    }

    setIsOptimizing(true);
    try {
      // Get more historical data for optimization
      const [historicalData1, historicalData2] = await Promise.all([
        fetchBinanceKlines(selectedSymbol1, selectedDays, 1000),
        fetchBinanceKlines(selectedSymbol2, selectedDays, 1000),
      ]);

      const optimized = optimizeParameters(historicalData1, historicalData2);

      if (optimized) {
        setOptimizationResults(optimized);
        showToast(
          "success",
          "Optimization Complete",
          `Best Sharpe Ratio: ${optimized.result.sharpeRatio.toFixed(2)}`
        );
      } else {
        showToast(
          "warning",
          "No Optimization Found",
          "Could not find better parameters"
        );
      }
    } catch (error) {
      showToast("error", "Optimization Failed", error.message);
    } finally {
      setIsOptimizing(false);
    }
  };

  const toggleBacktestPanel = () => {
    setShowBacktestPanel(!showBacktestPanel);
  };

  return (
    <div
      className="page-section"
      style={{ background: BINANCE_COLORS.primary, minHeight: "100vh" }}
    >
      {/* Control Panel */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BINANCE_COLORS.secondary} 0%, ${BINANCE_COLORS.primary} 100%)`,
          border: `1px solid ${BINANCE_COLORS.gray}`,
          borderRadius: "8px",
          padding: "24px",
          marginBottom: "24px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          <GitMerge size={20} style={{ color: BINANCE_COLORS.accent }} />
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: BINANCE_COLORS.textPrimary,
              margin: 0,
            }}
          >
            Real-time Pairs Trading Dashboard
          </h2>
          {showResults && (
            <div
              style={{
                fontSize: "12px",
                color: BINANCE_COLORS.green,
                background: `${BINANCE_COLORS.green}20`,
                padding: "4px 8px",
                borderRadius: "4px",
                marginLeft: "auto",
              }}
            >
              ● LIVE
            </div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 2fr 1fr 1fr auto",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {/* Enhanced Symbol Inputs */}
          <div className="form-group">
            <label
              className="form-label"
              style={{
                fontWeight: "600",
                color: BINANCE_COLORS.textPrimary,
                marginBottom: "8px",
                display: "block",
              }}
            >
              Primary Asset
            </label>
            <SymbolSearchInput
              value={searchSymbol1}
              onChange={(e) => setSearchSymbol1(e.target.value)}
              onFocus={() => setShowDropdown1(true)}
              onBlur={() => setTimeout(() => setShowDropdown1(false), 200)}
              placeholder="Search symbols..."
              filteredSymbols={filteredSymbols1}
              showDropdown={showDropdown1}
              onSelect={selectSymbol1}
            />
          </div>

          <div className="form-group">
            <label
              className="form-label"
              style={{
                fontWeight: "600",
                color: BINANCE_COLORS.textPrimary,
                marginBottom: "8px",
                display: "block",
              }}
            >
              Secondary Asset
            </label>
            <SymbolSearchInput
              value={searchSymbol2}
              onChange={(e) => setSearchSymbol2(e.target.value)}
              onFocus={() => setShowDropdown2(true)}
              onBlur={() => setTimeout(() => setShowDropdown2(false), 200)}
              placeholder="Search symbols..."
              filteredSymbols={filteredSymbols2}
              showDropdown={showDropdown2}
              onSelect={selectSymbol2}
            />
          </div>

          {/* Period Select */}
          <div className="form-group">
            <label
              className="form-label"
              style={{ fontWeight: "600", color: BINANCE_COLORS.textPrimary }}
            >
              Period
            </label>
            <select
              className="form-select"
              value={selectedDays}
              onChange={(e) => setSelectedDays(e.target.value)}
              style={{
                background: BINANCE_COLORS.primary,
                border: `2px solid ${BINANCE_COLORS.gray}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: BINANCE_COLORS.textPrimary,
                padding: "12px",
              }}
            >
              <option value="5m">5 Minutes</option>
              <option value="15m">15 Minutes</option>
              <option value="30m">30 Minutes</option>
              <option value="1h">1 Hour</option>
              <option value="4h">4 Hours</option>
              <option value="1d">1 Day</option>
              <option value="1w">1 Week</option>
            </select>
          </div>

          {/* Lookback Select */}
          <div className="form-group">
            <label
              className="form-label"
              style={{ fontWeight: "600", color: BINANCE_COLORS.textPrimary }}
            >
              Lookback
            </label>
            <select
              className="form-select"
              value={lookbackPeriod}
              onChange={(e) => setLookbackPeriod(parseInt(e.target.value))}
              style={{
                background: BINANCE_COLORS.primary,
                border: `2px solid ${BINANCE_COLORS.gray}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: BINANCE_COLORS.textPrimary,
                padding: "12px",
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          {/* Analyze Button */}
          <button
            onClick={() => {
              console.log("🔍 Find Tradeable Pairs button clicked");
              setShowLiveAnalysisPopup(true);
            }}
            style={{
              background: `linear-gradient(135deg, ${BINANCE_COLORS.blue} 0%, #1BABE6 100%)`,
              border: "none",
              borderRadius: "8px",
              color: "white",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: `0 4px 6px -1px ${BINANCE_COLORS.blue}40`,
              height: "fit-content",
              marginLeft: "12px",
            }}
          >
            🔍 Find Tradeable Pairs
          </button>
          {showLiveAnalysisPopup && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  background: BINANCE_COLORS.secondary,
                  borderRadius: "12px",
                  padding: "24px",
                  maxWidth: "600px",
                  width: "90%",
                  maxHeight: "80vh",
                  overflowY: "auto",
                  border: `1px solid ${BINANCE_COLORS.gray}`,
                }}
              >
                {/* Header with close button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      color: BINANCE_COLORS.textPrimary,
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Target size={20} color={BINANCE_COLORS.accent} />
                    Find Tradeable Pairs
                  </h3>
                  <button
                    onClick={() => {
                      console.log("❌ Closing popup");
                      setShowLiveAnalysisPopup(false);
                      setSelectedBaseCoin("");
                      setSearchBaseCoin("");
                      setFilteredBaseCoins([]);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: BINANCE_COLORS.textSecondary,
                      fontSize: "24px",
                      cursor: "pointer",
                      padding: "4px",
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Search input */}
                <div style={{ marginBottom: "20px", position: "relative" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: BINANCE_COLORS.textPrimary,
                      marginBottom: "8px",
                    }}
                  >
                    Search Base Coin:
                  </label>
                  <input
                    type="text"
                    placeholder="Type coin name (e.g., BTC, ETH, AAVE)..."
                    value={searchBaseCoin}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      console.log(`🔍 Searching for: ${value}`);
                      setSearchBaseCoin(value);

                      if (value.length > 0) {
                        const uniqueBaseAssets = [
                          ...new Set(
                            (priceAnalyzer.symbols || []).map(
                              (s) => s.baseAsset
                            )
                          ),
                        ];
                        const filtered = uniqueBaseAssets
                          .filter((asset) => asset.includes(value))
                          .sort()
                          .slice(0, 10);
                        setFilteredBaseCoins(filtered);
                        setShowBaseCoinDropdown(true);
                      } else {
                        setFilteredBaseCoins([]);
                        setShowBaseCoinDropdown(false);
                      }
                    }}
                    onFocus={() => {
                      if (searchBaseCoin.length > 0) {
                        setShowBaseCoinDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowBaseCoinDropdown(false), 200);
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: BINANCE_COLORS.primary,
                      border: `2px solid ${BINANCE_COLORS.gray}`,
                      borderRadius: "8px",
                      color: BINANCE_COLORS.textPrimary,
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />

                  {/* Search dropdown */}
                  {showBaseCoinDropdown && filteredBaseCoins.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: BINANCE_COLORS.secondary,
                        border: `1px solid ${BINANCE_COLORS.gray}`,
                        borderRadius: "8px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        zIndex: 1001,
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      {filteredBaseCoins.map((asset) => (
                        <div
                          key={asset}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            borderBottom: `1px solid ${BINANCE_COLORS.gray}40`,
                            transition: "background 0.2s ease",
                            color: BINANCE_COLORS.textPrimary,
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.background = `${BINANCE_COLORS.blue}20`)
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.background = "transparent")
                          }
                          onClick={() => {
                            console.log(`✅ Selected coin: ${asset}`);
                            setSelectedBaseCoin(asset);
                            setSearchBaseCoin(asset);
                            setShowBaseCoinDropdown(false);
                            setFilteredBaseCoins([]);
                          }}
                        >
                          <div style={{ fontWeight: "600" }}>{asset}</div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: BINANCE_COLORS.textSecondary,
                              marginTop: "2px",
                            }}
                          >
                            {
                              (priceAnalyzer.symbols || []).filter(
                                (s) => s.baseAsset === asset
                              ).length
                            }{" "}
                            pairs available
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Find pairs button */}
                <button
                  onClick={() => {
                    console.log(
                      `🎯 Button clicked with selectedBaseCoin: ${selectedBaseCoin}`
                    );
                    if (selectedBaseCoin) {
                      findTradeablePairs(selectedBaseCoin);
                    } else {
                      console.log("❌ No base coin selected");
                    }
                  }}
                  disabled={!selectedBaseCoin || analyzingPairs}
                  style={{
                    width: "100%",
                    background: `linear-gradient(135deg, ${BINANCE_COLORS.green} 0%, #018A5B 100%)`,
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor:
                      !selectedBaseCoin || analyzingPairs
                        ? "not-allowed"
                        : "pointer",
                    opacity: !selectedBaseCoin || analyzingPairs ? 0.6 : 1,
                    marginBottom: "20px",
                  }}
                >
                  {analyzingPairs
                    ? "Analyzing Pairs..."
                    : "Find Tradeable Pairs"}
                </button>

                {/* Results section */}
                {tradeablePairs.length > 0 && (
                  <div>
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: BINANCE_COLORS.textPrimary,
                        marginBottom: "16px",
                      }}
                    >
                      Potential Pairs with {selectedBaseCoin} (Z-Score
                      Analysis):
                    </h4>
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        display: "grid",
                        gap: "8px",
                      }}
                    >
                      {tradeablePairs.map((pair, index) => (
                        <div
                          key={pair.symbol}
                          onClick={() => {
                            // Auto-fill the main form with this pair
                            setSearchSymbol1(selectedBaseCoin + "USDT");
                            setSelectedSymbol1(selectedBaseCoin + "USDT");
                            setSearchSymbol2(pair.symbol);
                            setSelectedSymbol2(pair.symbol);
                            setShowLiveAnalysisPopup(false);
                            console.log(
                              `🔄 Auto-filled pair: ${selectedBaseCoin}USDT vs ${pair.symbol}`
                            );
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            background: BINANCE_COLORS.primary,
                            borderRadius: "8px",
                            border: `1px solid ${BINANCE_COLORS.gray}`,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = `${BINANCE_COLORS.blue}10`)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              BINANCE_COLORS.primary)
                          }
                        >
                          <div>
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: BINANCE_COLORS.textPrimary,
                              }}
                            >
                              {pair.symbol}
                            </span>
                            <div
                              style={{
                                fontSize: "12px",
                                color: BINANCE_COLORS.textSecondary,
                                marginTop: "2px",
                              }}
                            >
                              Pair: {selectedBaseCoin} ↔ {pair.baseAsset}
                            </div>
                            <div
                              style={{
                                fontSize: "10px",
                                color: BINANCE_COLORS.accent,
                                marginTop: "2px",
                              }}
                            >
                              💡 Click to use this pair
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: BINANCE_COLORS.blue,
                                fontFamily: "monospace",
                              }}
                            >
                              Z-Score: {pair.zScore}
                            </div>
                            <div
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                background:
                                  pair.tradeability === "High"
                                    ? `${BINANCE_COLORS.green}20`
                                    : pair.tradeability === "Medium"
                                    ? `${BINANCE_COLORS.accent}20`
                                    : `${BINANCE_COLORS.blue}20`,
                                color:
                                  pair.tradeability === "High"
                                    ? BINANCE_COLORS.green
                                    : pair.tradeability === "Medium"
                                    ? BINANCE_COLORS.accent
                                    : BINANCE_COLORS.blue,
                                fontWeight: "600",
                              }}
                            >
                              {pair.tradeability}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading section */}
                {analyzingPairs && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: BINANCE_COLORS.textSecondary,
                    }}
                  >
                    <div
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        border: `2px solid ${BINANCE_COLORS.blue}`,
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    <p style={{ marginTop: "12px", fontSize: "14px" }}>
                      Analyzing pairs for tradeable opportunities...
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            onClick={handlePairAnalysis}
            disabled={isAnalyzing}
            style={{
              background: `linear-gradient(135deg, ${BINANCE_COLORS.accent} 0%, #E6B800 100%)`,
              border: "none",
              borderRadius: "8px",
              color: BINANCE_COLORS.primary,
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "700",
              cursor: isAnalyzing ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              boxShadow: `0 4px 6px -1px ${BINANCE_COLORS.accent}40`,
              height: "fit-content",
            }}
          >
            {isAnalyzing ? "Analyzing..." : "Start Live Analysis"}
          </button>
        </div>
        {/* Backtest Toggle Button */}
        {showResults && (
          <div style={{ marginTop: "16px", textAlign: "right" }}>
            <button
              onClick={toggleBacktestPanel}
              style={{
                background: showBacktestPanel
                  ? `linear-gradient(135deg, ${BINANCE_COLORS.red} 0%, #CC3A50 100%)`
                  : `linear-gradient(135deg, ${BINANCE_COLORS.green} 0%, #018A5B 100%)`,
                border: "none",
                borderRadius: "6px",
                color: "white",
                padding: "8px 16px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <BarChart3 size={14} />
              {showBacktestPanel ? "Hide Backtest" : "Show Backtest"}
            </button>
          </div>
        )}
      </div>

      {showResults && (
        <>
          {/* Asset Info Header */}
          <AssetInfoHeader
            symbol1={selectedSymbol1}
            symbol2={selectedSymbol2}
            tickerData1={tickerData1}
            tickerData2={tickerData2}
            realTimeMetrics={realTimeMetrics}
          />

          {/* Backtest Panel */}
          {showBacktestPanel && (
            <>
              <BacktestSettingsPanel
                settings={backtestSettings}
                onSettingsChange={setBacktestSettings}
                onRunBacktest={handleRunBacktest}
                isBacktesting={isBacktesting}
                onOptimize={handleOptimizeParameters}
                isOptimizing={isOptimizing}
                optimizationResults={optimizationResults}
              />

              {backtestResults && (
                <BacktestResults
                  results={backtestResults}
                  symbol1={selectedSymbol1}
                  symbol2={selectedSymbol2}
                />
              )}
            </>
          )}

          {/* Main Dashboard Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
              marginBottom: "24px",
            }}
          >
            {/* Cumulative Returns Chart */}
            <div
              style={{
                background: BINANCE_COLORS.secondary,
                border: `1px solid ${BINANCE_COLORS.gray}`,
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: BINANCE_COLORS.textPrimary,
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <TrendingUp size={18} color={BINANCE_COLORS.accent} />
                Live Cumulative Returns
              </h4>
              <CumulativeReturnsChart
                data={timeSeriesData}
                symbol1={selectedSymbol1}
                symbol2={selectedSymbol2}
              />
            </div>

            {/* Spread & Z-Score Chart */}
            <div
              style={{
                background: BINANCE_COLORS.secondary,
                border: `1px solid ${BINANCE_COLORS.gray}`,
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: BINANCE_COLORS.textPrimary,
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <BarChart3 size={18} color={BINANCE_COLORS.accent} />
                Live Spread & Z-Score
              </h4>
              <SpreadZScoreChart
                data={timeSeriesData}
                currentZScore={realTimeMetrics.zscore}
              />
            </div>
          </div>

          {/* Bottom Analytics Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
              gap: "24px",
            }}
          >
            {/* Enhanced Statistical Metrics */}
            <StatisticalMetricsPanel
              realTimeData={realTimeMetrics}
              symbol1={selectedSymbol1}
              symbol2={selectedSymbol2}
              priceData1={priceData1}
              priceData2={priceData2}
            />

            {/* Copula Analysis */}
            <CopulaAnalysis
              data={copulaData}
              symbol1={selectedSymbol1}
              symbol2={selectedSymbol2}
            />

            {/* Dynamic Correlation Chart */}
            <div
              style={{
                background: BINANCE_COLORS.secondary,
                border: `1px solid ${BINANCE_COLORS.gray}`,
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: BINANCE_COLORS.textPrimary,
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Activity size={16} color={BINANCE_COLORS.accent} />
                Live Dynamic Correlation
              </h4>
              <DynamicCorrelationChart data={timeSeriesData} />
            </div>
          </div>

          {/* Backtest Summary (when results exist but panel is hidden) */}
          {backtestResults && !showBacktestPanel && (
            <div
              style={{
                background: `linear-gradient(135deg, ${BINANCE_COLORS.secondary} 0%, ${BINANCE_COLORS.primary} 100%)`,
                border: `1px solid ${BINANCE_COLORS.green}`,
                borderRadius: "8px",
                padding: "16px",
                marginTop: "24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: BINANCE_COLORS.textPrimary,
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <BarChart3 size={16} color={BINANCE_COLORS.green} />
                  Latest Backtest Results
                </h4>
                <div
                  style={{
                    display: "flex",
                    gap: "24px",
                    fontSize: "12px",
                    color: BINANCE_COLORS.textSecondary,
                  }}
                >
                  <span>
                    Total Return:{" "}
                    <strong
                      style={{
                        color:
                          backtestResults.totalReturn >= 0
                            ? BINANCE_COLORS.green
                            : BINANCE_COLORS.red,
                      }}
                    >
                      {backtestResults.totalReturn.toFixed(2)}%
                    </strong>
                  </span>
                  <span>
                    Win Rate:{" "}
                    <strong style={{ color: BINANCE_COLORS.blue }}>
                      {backtestResults.winRate.toFixed(1)}%
                    </strong>
                  </span>
                  <span>
                    Total Trades:{" "}
                    <strong style={{ color: BINANCE_COLORS.textPrimary }}>
                      {backtestResults.totalTrades}
                    </strong>
                  </span>
                  <span>
                    Sharpe Ratio:{" "}
                    <strong style={{ color: BINANCE_COLORS.purple }}>
                      {backtestResults.sharpeRatio.toFixed(2)}
                    </strong>
                  </span>
                </div>
              </div>
              <button
                onClick={toggleBacktestPanel}
                style={{
                  background: `linear-gradient(135deg, ${BINANCE_COLORS.green} 0%, #018A5B 100%)`,
                  border: "none",
                  borderRadius: "6px",
                  color: "white",
                  padding: "8px 16px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                View Details
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PairTrading;
